import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import type { PlayerView } from '@bcm/rules';
import { bindFxLayer, bindShowcase, type ShowcaseRequest } from '../director/fx';
import { obscureBoard } from '../scene/pacing';
import { useGame } from '../state/store';
import { C, SEAL_COLORS, defName, opensRoom, pack } from '../game/content';
import { LOCATION_ICON, resolutionIcon } from '../game/icons';
import { Icon, SeatEmblem } from './Icon';
import { Card, RichText, tapPreview, usePreview } from './Card';
import { audio } from '../audio/sfx';

export function FxLayer({ view }: { view: PlayerView }) {
  const ref = useRef<HTMLDivElement>(null);
  const [show, setShow] = useState<(ShowcaseRequest & { resolve: () => void; key: number }) | null>(null);
  const stage = useRef<HTMLDivElement>(null);
  const key = useRef(0);

  useEffect(() => {
    bindFxLayer(ref.current);
    bindShowcase((req) => new Promise<void>((resolve) => setShow({ ...req, resolve, key: ++key.current })));
    return () => {
      bindFxLayer(null);
      bindShowcase(null);
    };
  }, []);

  useLayoutEffect(() => {
    if (!show || !stage.current) return;
    const el = stage.current;
    const card = el.querySelector('.showcase-card');
    const stamp = el.querySelector('.showcase-stamp');
    const flare = el.querySelector('.showcase-flare');
    const tl = gsap.timeline({
      onComplete: () => {
        const done = show.resolve;
        setShow(null);
        done();
      },
    });
    tl.timeScale(show.speed);
    audio.play('card');
    // Offsets from the screen centre (where the card resolves) to its source and destination.
    const rel = (p: { x: number; y: number }) => ({ x: p.x - innerWidth / 2, y: p.y - innerHeight / 2 });
    const from = show.from ? rel(show.from) : null;
    const to = show.to ? rel(show.to) : null;
    const t0 = from ? 0.12 : 0;
    const stamped = 0.59 + t0;
    // The card leaves after its beat, or once it has rested stamped for `show.hold` real seconds
    // (the timeline runs at `speed`, so that is `hold × speed` on it), whichever is later.
    const leave = Math.max(t0 + (show.small ? 1.0 : 1.35), stamped + (show.hold ?? 0) * show.speed);
    tl.fromTo(el, { opacity: 0 }, { opacity: 1, duration: 0.12 }, 0);
    if (from) {
      // Out of the player's panel (or hand), across to the centre.
      tl.fromTo(card, { x: from.x, y: from.y, scale: 0.16, rotate: -8, opacity: 0 }, { x: 0, y: 0, scale: 1, rotate: 0, opacity: 1, duration: 0.47, ease: 'power3.out' }, 0);
    } else {
      tl.fromTo(card, { y: 160, scale: 0.6, rotate: -6, opacity: 0 }, { y: 0, scale: 1, rotate: 0, opacity: 1, duration: 0.35, ease: 'back.out(1.6)' }, 0);
    }
    tl.fromTo(stamp, { scale: 2.6, opacity: 0, rotate: -18 }, { scale: 1, opacity: 1, rotate: -12, duration: 0.14, ease: 'power4.in' }, 0.45 + t0)
      .call(() => audio.play('stamp'), [], 0.58 + t0)
      // Deferred renders: these must not touch the card or flare until their moment comes.
      .fromTo(flare, { scale: 0.2, opacity: 0.9 }, { scale: 2.4, opacity: 0, duration: 0.6, ease: 'expo.out', immediateRender: false }, stamped)
      .fromTo(card, { x: 0 }, { x: 3, duration: 0.04, yoyo: true, repeat: 3, immediateRender: false }, stamped);
    gsap.set(flare, { opacity: 0 });
    if (to) {
      // Then away to where it ends up (a tableau, the discard pile).
      tl.to(card, { x: to.x, y: to.y, scale: 0.14, rotate: 6, duration: 0.42, ease: 'power2.in' }, leave)
        .to(card, { opacity: 0, duration: 0.12 }, leave + 0.32);
    } else {
      tl.to(el, { opacity: 0, duration: 0.25 }, leave).to(card, { y: 120, scale: 0.5, duration: 0.3, ease: 'power2.in' }, leave);
    }
    return () => {
      tl.kill();
    };
  }, [show]);

  return (
    <div className="fx-layer" ref={ref} aria-hidden>
      {show && (
        <div className="showcase" ref={stage} key={show.key}>
          <div className="showcase-flare" style={{ background: `radial-gradient(circle, ${show.color}cc 0%, transparent 65%)` }} />
          <div className="showcase-card">
            <Card def={show.def} view={view} size="lg" />
            <div className="showcase-stamp" style={{ color: show.color, borderColor: show.color }}>
              {show.stamp}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function Toasts() {
  const toasts = useGame((s) => s.toasts);
  return (
    <div className="toasts" role="status" aria-live="polite">
      {toasts.map((t) => (
        <div key={t.id} className={`toast toast-${t.kind}`}>
          {t.text}
        </div>
      ))}
    </div>
  );
}

/**
 * A Ledger line with every card it names made inspectable: hover (or tap) a name for the full card,
 * so a play you missed can be read afterwards.
 */
function LogText({ text, defs }: { text: string; defs: string[] }) {
  const show = usePreview((s) => s.show);
  const hide = usePreview((s) => s.hide);
  // Split the line at each card name, in order.
  const parts: (string | { def: string; name: string })[] = [];
  let rest = text;
  for (const def of defs) {
    const name = defName(def);
    const i = name ? rest.indexOf(name) : -1;
    if (i < 0) continue;
    if (i > 0) parts.push(rest.slice(0, i));
    parts.push({ def, name });
    rest = rest.slice(i + name.length);
  }
  if (rest) parts.push(rest);
  // A line that comes from a card without naming it ("+{R}" from an ability, a starting package):
  // the whole line opens that card.
  if (defs.length && parts.every((p) => typeof p === 'string')) {
    const def = defs[0]!;
    return (
      <span
        className="log-card whole"
        role="button"
        tabIndex={0}
        onMouseEnter={(e) => show(def, undefined, e.currentTarget.getBoundingClientRect())}
        onMouseLeave={hide}
        onFocus={(e) => show(def, undefined, e.currentTarget.getBoundingClientRect())}
        onBlur={hide}
        {...tapPreview(def)}
      >
        <RichText text={text} />
      </span>
    );
  }
  return (
    <>
      {parts.map((p, i) =>
        typeof p === 'string' ? (
          <RichText key={i} text={p} />
        ) : (
          <span
            key={i}
            className="log-card"
            role="button"
            tabIndex={0}
            onMouseEnter={(e) => show(p.def, undefined, e.currentTarget.getBoundingClientRect())}
            onMouseLeave={hide}
            onFocus={(e) => show(p.def, undefined, e.currentTarget.getBoundingClientRect())}
            onBlur={hide}
            {...tapPreview(p.def)}
          >
            {p.name}
          </span>
        ),
      )}
    </>
  );
}

export function GameLog() {
  const log = useGame((s) => s.log);
  const show = useGame((s) => s.settings.showLog);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.scrollTo({ top: ref.current.scrollHeight });
  }, [log.length, open]);
  if (!show) return null;
  const last = log[log.length - 1];
  return (
    <section className={`panel log ${open ? 'is-open' : ''}`} aria-label="Game log">
      <button className="log-toggle" onClick={() => setOpen(!open)} aria-expanded={open}>
        <h3>Ledger {open ? '▾' : '▸'}</h3>
        {!open && last && (
          <span className="log-last">
            <RichText text={last.text} />
          </span>
        )}
      </button>
      <div className="log-lines" ref={ref} role="log" aria-live="polite" hidden={!open}>
        {log.slice(-80).map((l) => (
          <div key={`${l.seq}-${l.text}`} className={`log-line ${l.text.startsWith('—') ? 'round' : ''}`} style={l.seat !== undefined ? ({ '--seal': SEAL_COLORS[l.seat] } as React.CSSProperties) : undefined}>
            <LogText text={l.text} defs={l.defs ?? []} />
          </div>
        ))}
      </div>
    </section>
  );
}

/** Counts up from 0 to `to` once `start` is true. */
function CountUp({ to, start, duration = 900 }: { to: number; start: boolean; duration?: number }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!start) return;
    let raf = 0;
    const t0 = performance.now();
    const step = () => {
      const k = Math.min(1, (performance.now() - t0) / duration);
      setN(Math.round(to * (1 - Math.pow(1 - k, 3))));
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [to, start, duration]);
  return <>{n}</>;
}

/**
 * The final audit, staged: players are revealed from last place to first, each score counting up
 * with its breakdown, secret agendas flip open, and the winner gets the spotlight.
 */
export function Results({ view }: { view: PlayerView }) {
  const busy = useGame((s) => s.busy);
  const room = useGame((s) => s.room);
  const you = useGame((s) => s.you);
  const mode = useGame((s) => s.mode);
  const lobby = useGame((s) => s.lobby);
  const leave = useGame((s) => s.leave);
  const startOffline = useGame((s) => s.startOffline);
  const reduced = useGame((s) => s.settings.reducedMotion);
  const [hidden, setHidden] = useState(false);
  const [revealed, setRevealed] = useState(0);
  const ended = view.phase === 'ended' && !!view.result && !busy;
  const count = view.result?.standings.length ?? 0;

  useEffect(() => {
    if (!ended) return;
    if (reduced) return setRevealed(count + 1);
    let i = 0;
    const next = () => {
      i++;
      setRevealed(i);
      if (i <= count) audio.play(i === count ? 'fanfare' : 'reveal');
      if (i <= count) timer = window.setTimeout(next, i === count ? 1400 : 1100);
    };
    let timer = window.setTimeout(next, 500);
    return () => clearTimeout(timer);
  }, [ended, count, reduced]);
  // The results cover the board: it idles at a low frame rate behind them.
  useEffect(() => (ended && !hidden ? obscureBoard() : undefined), [ended, hidden]);

  if (!ended) return null;
  if (hidden) {
    return (
      <button className="results-reopen gilt-btn small" onClick={() => setHidden(false)}>
        Show results
      </button>
    );
  }
  const standings = [...view.result!.standings].sort((a, b) => a.place - b.place);
  const winner = standings[0]!;
  const isHost = room && you && room.hostSeat === you.seat;
  const done = revealed > count;
  // Reveal order: last place first.
  const revealIndex = (place: number, idx: number) => standings.length - idx;
  return (
    <div className="modal-backdrop results-backdrop">
      <div className={`modal results ${done ? 'done' : ''}`}>
        <div className="results-crest">
          <Icon name="quill" size={40} />
        </div>
        <h2 className={`results-title ${done ? 'in' : ''}`}>
          {done ? (
            <>
              {view.seats[winner.seat]!.name} is <em>The Quill Eternal</em>
            </>
          ) : (
            'The Final Audit'
          )}
        </h2>
        <p className="hint">Closing time at the Ministry. The Grand Auditor tallies the Ledger.</p>
        <div className="res-list">
          {standings.map((st, idx) => {
            const seat = view.seats[st.seat]!;
            const shown = revealed >= revealIndex(st.place, idx);
            const agenda = st.breakdown.find((b) => b.label.startsWith('Agenda: '));
            const parts = st.breakdown.filter((b) => b.vp !== 0 && !b.label.startsWith('Agenda: '));
            return (
              <div key={st.seat} className={`res-row ${shown ? 'shown' : ''} ${st.place === 1 && done ? 'winner' : ''}`} style={{ '--seal': SEAL_COLORS[st.seat] } as React.CSSProperties}>
                <span className="res-place">{st.place}</span>
                <SeatEmblem seat={st.seat} size={34} />
                <div className="res-who">
                  <div className="res-name">{seat.name}</div>
                  <div className="res-sub">
                    {seat.identity ? C.identities.get(seat.identity)?.name : ''} · {st.place === 1 ? 'The Quill Eternal' : pack.ranks[seat.rank]?.title}
                  </div>
                  <div className="res-parts">
                    {parts.map((b) => (
                      <span key={b.label} className="res-part">
                        {b.label} <b>{b.vp}</b>
                      </span>
                    ))}
                    {agenda && (
                      <span className={`res-agenda ${st.agendaMet ? 'met' : 'missed'}`} data-tip={st.agenda ? `${C.agendas.get(st.agenda)?.name}|${C.agendas.get(st.agenda)?.text}` : undefined}>
                        <Icon name="scroll" size={12} /> {agenda.label.replace('Agenda: ', '')} {st.agendaMet ? <b>+{agenda.vp}</b> : <i>missed</i>}
                      </span>
                    )}
                  </div>
                </div>
                <span className="res-vp">
                  <CountUp to={st.vp} start={shown} /> <small>VP</small>
                </span>
              </div>
            );
          })}
        </div>
        {(view.resolutions?.inForce.length ?? 0) > 0 && (
          <div className={`res-resolutions ${done ? 'in' : ''}`} aria-label="Resolutions passed this game">
            <span className="res-resolutions-label">Passed this game</span>
            {view.resolutions!.inForce.map((r) => {
              const room = opensRoom(r.resolution);
              return (
                <span key={r.resolution} className="res-plaque small" style={{ '--seal': SEAL_COLORS[r.by] } as React.CSSProperties} data-tip={`${defName(r.resolution)}|${C.resolutions.get(r.resolution)?.rules ?? ''}`}>
                  <span className="res-plaque-medal">
                    <Icon name={room ? LOCATION_ICON[room] : resolutionIcon(r.resolution)} size={12} />
                  </span>
                  <span className="res-plaque-name">{defName(r.resolution)}</span>
                </span>
              );
            })}
          </div>
        )}
        <div className={`row results-actions ${done ? 'in' : ''}`}>
          <button onClick={() => setHidden(true)}>View board</button>
          {room?.report && (
            <button
              data-tip="Game summary|The playtest record of this game (docs/09 §10.1), as a JSON file."
              onClick={() => {
                const blob = new Blob([JSON.stringify(room.report, null, 2)], { type: 'application/json' });
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = `bureaucromancy-${room.code}-${new Date().toISOString().slice(0, 16).replace(/[:T]/g, '-')}.json`;
                a.click();
                setTimeout(() => URL.revokeObjectURL(a.href), 1000);
              }}
            >
              Game summary
            </button>
          )}
          {mode === 'offline' && (
            <button className="gilt-btn primary" onClick={() => startOffline(view.playerCount - 1, view.options.cardValues === 'v1' ? 'v1' : 'v2', view.options.resolutions !== false)}>
              Play again
            </button>
          )}
          {mode === 'online' && isHost && (
            <button className="gilt-btn primary" onClick={() => lobby({ op: 'rematch' })}>
              Rematch
            </button>
          )}
          <button onClick={leave}>Main menu</button>
        </div>
      </div>
    </div>
  );
}
